import { z } from "zod";

/**
 * Admin 2FA + password self-service DTO (US-AE-IMP.F3-0112 · @UC9139/@UC9140/@UC9141).
 *
 * Một nguồn sự thật client↔server cho:
 *  - Enroll 2FA (otpauth + recovery codes hiện MỘT LẦN)      — @UC9139
 *  - Confirm 2FA (TOTP 6 số → set confirmedAt)               — @UC9139
 *  - Regen recovery codes (khi đã confirm)                   — @UC9139
 *  - Reset 2FA người khác (SUPER_ADMIN)                      — @UC9140
 *  - Đổi mật khẩu (current → next)                           — @UC9141
 *  - Reset mật khẩu quên (request/confirm, enumeration-safe) — @UC9141
 *
 * BẢO MẬT (HARD invariants):
 * - KHÔNG nhúng secret/hash/recovery-plaintext vào bất kỳ RESPONSE type nào ngoài enroll/regen
 *   (recovery codes chỉ hiện MỘT LẦN).
 * - Password policy (`next`) KHÔNG enforce ở tầng Zod request (giữ `min(1)`) để backend phân biệt
 *   400 (thiếu field) vs 422 (yếu). Độ mạnh chốt bằng `isStrongAdminPassword` (backend là nguồn thật).
 */

// ── Chính sách mật khẩu (PO DECIDED 06/07/2026: ≥12 ký tự + không nằm trong danh sách phổ biến) ──

/** Độ dài tối thiểu mật khẩu admin mới. */
export const ADMIN_PASSWORD_MIN_LENGTH = 12;

/** Số lượng recovery code sinh khi enroll (PO DECIDED: 10 mã, single-use). */
export const RECOVERY_CODE_COUNT = 10;

/** TTL token reset-quên mật khẩu admin (PO DECIDED: 15 phút, single-use). */
export const ADMIN_PASSWORD_RESET_TTL_MINUTES = 15;

/**
 * Chuỗi con "phổ biến" bị chặn (case-insensitive substring). Danh sách nhỏ, có chủ đích —
 * bắt các mật khẩu yếu điển hình (password1234, superadmin!, goppy2026…). KHÔNG hardcode secret
 * thật (G4) — đây là danh sách chặn công khai. Backend là nguồn thật (trả 422).
 */
export const COMMON_PASSWORD_SUBSTRINGS: readonly string[] = [
  "password",
  "qwerty",
  "123456",
  "111111",
  "000000",
  "letmein",
  "welcome",
  "iloveyou",
  "abc123",
  "admin",
  "superadmin",
  "changeme",
  "goppy",
];

/**
 * Mật khẩu admin "đủ mạnh" khi: dài ≥ ADMIN_PASSWORD_MIN_LENGTH VÀ không chứa chuỗi phổ biến.
 * Pure — dùng chung backend (enforce 422) + admin UI (validate sớm). KHÔNG log giá trị.
 */
export function isStrongAdminPassword(password: string): boolean {
  if (password.length < ADMIN_PASSWORD_MIN_LENGTH) return false;
  const lower = password.toLowerCase();
  return !COMMON_PASSWORD_SUBSTRINGS.some((frag) => lower.includes(frag));
}

// ── @UC9139 — Enroll 2FA ──────────────────────────────────────────────────────

/**
 * Response enroll (@UC9139, AC1): otpauth URI (cho QR) + N recovery codes hiện MỘT LẦN.
 * recoveryCodes chỉ tồn tại ở response NÀY (và regen) — không endpoint khác trả lại.
 */
export const adminEnrollResponseSchema = z.object({
  otpauthUri: z.string().min(1),
  recoveryCodes: z.array(z.string().min(1)).nonempty(),
});
export type AdminEnrollResponse = z.infer<typeof adminEnrollResponseSchema>;

/** Response regen recovery codes (@UC9139) — chỉ recovery codes mới hiện MỘT LẦN. */
export const adminRegenRecoveryResponseSchema = z.object({
  recoveryCodes: z.array(z.string().min(1)).nonempty(),
});
export type AdminRegenRecoveryResponse = z.infer<typeof adminRegenRecoveryResponseSchema>;

/** Body confirm 2FA (@UC9139, AC2): mã TOTP 6 số. Sai định dạng → parse fail (400 biên). */
export const adminConfirm2faRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Mã TOTP phải gồm 6 chữ số"),
});
export type AdminConfirm2faRequest = z.infer<typeof adminConfirm2faRequestSchema>;

/** Response confirm 2FA (@UC9139): trạng thái đã bật — KHÔNG chứa secret. */
export const adminConfirm2faResponseSchema = z.object({
  status: z.literal("confirmed"),
});
export type AdminConfirm2faResponse = z.infer<typeof adminConfirm2faResponseSchema>;

// ── @UC9140 — Reset 2FA người khác (SUPER_ADMIN) ──────────────────────────────

/** Body reset-2fa (@UC9140): reason tuỳ chọn (audit). KHÔNG leak trạng thái target. */
export const adminReset2faRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type AdminReset2faRequest = z.infer<typeof adminReset2faRequestSchema>;

/** Response reset-2fa (@UC9140): CHỈ status generic — không secret/session token/target-state. */
export const adminReset2faResponseSchema = z.object({
  status: z.literal("reset"),
});
export type AdminReset2faResponse = z.infer<typeof adminReset2faResponseSchema>;

// ── @UC9141 — Đổi mật khẩu ─────────────────────────────────────────────────────

/**
 * Body đổi mật khẩu (@UC9141, AC3): current + next. `next` giữ `min(1)` ở Zod (không enforce
 * policy) để backend phân biệt 400 (thiếu) vs 422 (yếu — isStrongAdminPassword).
 */
export const adminPasswordChangeRequestSchema = z.object({
  current: z.string().min(1),
  next: z.string().min(1),
});
export type AdminPasswordChangeRequest = z.infer<typeof adminPasswordChangeRequestSchema>;

/** Response đổi mật khẩu (@UC9141) — trạng thái generic, KHÔNG trả password thô. */
export const adminPasswordChangeResponseSchema = z.object({
  status: z.literal("changed"),
});
export type AdminPasswordChangeResponse = z.infer<typeof adminPasswordChangeResponseSchema>;

// ── @UC9141 — Reset mật khẩu quên (enumeration-safe) ──────────────────────────

/** Body reset-request (@UC9141, AC4): chỉ email. Response luôn generic (enumeration-safe). */
export const adminPasswordResetRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.string().email()),
});
export type AdminPasswordResetRequest = z.infer<typeof adminPasswordResetRequestSchema>;

/** Body reset-confirm (@UC9141, AC5): token + next. `next` policy enforce ở backend (422). */
export const adminPasswordResetConfirmSchema = z.object({
  token: z.string().min(1),
  next: z.string().min(1),
});
export type AdminPasswordResetConfirm = z.infer<typeof adminPasswordResetConfirmSchema>;

// ── @UC9151 — Onboard: đặt mật khẩu lần đầu (invited→active) ───────────────────

/**
 * Body accept-invite (US-AE-IMP.F3-0144-S01 · @UC9151): token onboard + password.
 * Field `password` (KHÔNG `next` như reset-QUÊN) để phân biệt NGỮ NGHĨA onboard vs reset. `password`
 * giữ `min(1)` ở Zod (KHÔNG enforce policy) → backend phân biệt 400 (thiếu field) vs 422 (yếu —
 * `isStrongAdminPassword` là nguồn thật). Mirror `adminPasswordResetConfirmSchema`.
 */
export const adminAcceptInviteSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(1),
});
export type AdminAcceptInvite = z.infer<typeof adminAcceptInviteSchema>;

/** Response generic reset-request/confirm (enumeration-safe) — CHỈ message, KHÔNG token/hash. */
export const adminGenericMessageResponseSchema = z.object({
  message: z.string().min(1),
});
export type AdminGenericMessageResponse = z.infer<typeof adminGenericMessageResponseSchema>;

// ── Hằng message/code (DRY api↔admin) ─────────────────────────────────────────

/** 401 — mật khẩu hiện tại sai khi đổi mật khẩu (@UC9141, AC2). */
export const ADMIN_CURRENT_PASSWORD_INVALID_CODE = "INVALID_CURRENT_PASSWORD";
export const ADMIN_CURRENT_PASSWORD_INVALID_MESSAGE = "Mật khẩu hiện tại không đúng";

/** 422 — mật khẩu mới không đạt chính sách độ mạnh (@UC9141, AC3). */
export const ADMIN_WEAK_PASSWORD_CODE = "WEAK_PASSWORD";
export const ADMIN_WEAK_PASSWORD_MESSAGE =
  "Mật khẩu mới phải dài ít nhất 12 ký tự và không nằm trong danh sách phổ biến";

/** 400 — token reset không hợp lệ / hết hạn / đã dùng (@UC9141, AC5) — generic (không leak). */
export const ADMIN_RESET_TOKEN_INVALID_CODE = "INVALID_RESET_TOKEN";
export const ADMIN_RESET_TOKEN_INVALID_MESSAGE =
  "Liên kết đặt lại mật khẩu không hợp lệ hoặc đã hết hạn";

/** 409 — đã bật 2FA (confirmed) khi gọi enroll lại — dùng regen thay vì enroll (@UC9139). */
export const ADMIN_2FA_ALREADY_ENROLLED_CODE = "TWOFA_ALREADY_ENROLLED";
export const ADMIN_2FA_ALREADY_ENROLLED_MESSAGE = "Tài khoản đã bật xác thực hai lớp";

/** Thông điệp generic reset-request (enumeration-safe — không lộ email tồn tại/allowlist). */
export const ADMIN_PASSWORD_RESET_GENERIC_MESSAGE =
  "Nếu email hợp lệ, chúng tôi đã gửi hướng dẫn đặt lại mật khẩu.";

/** Thông điệp thành công đổi/đặt lại mật khẩu. */
export const ADMIN_PASSWORD_RESET_SUCCESS_MESSAGE = "Đặt lại mật khẩu thành công.";

// ── @UC9151 — Onboard accept-invite (US-AE-IMP.F3-0144) ───────────────────────

/**
 * Thông điệp thành công accept-invite (đặt mật khẩu lần đầu). Accept-invite KHÔNG cấp session
 * (PO §6.4) → client tự điều hướng /login. DRY api↔admin.
 */
export const ADMIN_ACCEPT_INVITE_SUCCESS_MESSAGE =
  "Đặt mật khẩu thành công. Vui lòng đăng nhập.";

/**
 * 400 — token onboard (accept-invite) không hợp lệ / hết hạn / đã dùng (@UC9151) — generic
 * (enumeration-safe, không leak tồn tại admin). TÁCH mã khỏi reset-QUÊN (`INVALID_RESET_TOKEN`) để
 * rõ ngữ nghĩa UX/audit onboard vs reset. KHÔNG trả password thô.
 */
export const ADMIN_INVITE_TOKEN_INVALID_CODE = "INVALID_INVITE_TOKEN";
export const ADMIN_INVITE_TOKEN_INVALID_MESSAGE =
  "Liên kết kích hoạt tài khoản không hợp lệ hoặc đã hết hạn";
